import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, MapPin, Crosshair, Backpack, Mountain, CalendarDays, ArrowRight, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { STATE_HERO, STATE_HERO_DEFAULT, SPECIES_ICON } from "@/lib/stateMeta";

const Chip = ({ icon: Ic, label, value, testid }) => (
  <div className="border border-bone/15 rounded-md px-4 py-3 bg-bone/[0.03]" data-testid={testid}>
    <span className="text-[11px] text-bone/50 flex items-center gap-1.5"><Ic size={12} className="text-blaze" /> {label}</span>
    <p className="text-sm font-semibold text-bone mt-1">{value || "—"}</p>
  </div>
);

export default function SharedHunt() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [hunt, setHunt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    api.sharedHunt(shareId)
      .then((d) => { if (active) setHunt(d); })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [shareId]);

  if (loading) return <div className="bg-paper min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div></div>;

  if (notFound || !hunt) return (
    <div className="bg-paper min-h-screen"><SiteHeader />
      <div className="py-32 grid place-items-center text-center px-6" data-testid="shared-hunt-notfound">
        <div>
          <p className="font-serif text-3xl text-charcoal">This hunt isn{"\u2019"}t available.</p>
          <p className="text-charcoal/60 mt-2">The link may have expired or the hunt was made private.</p>
          <button onClick={() => navigate("/")} className="mt-6 bg-blaze text-white px-6 py-3 rounded-md font-semibold inline-flex items-center gap-2" data-testid="shared-hunt-home">Explore HomeRange <ArrowRight size={15} /></button>
        </div>
      </div>
      <Footer />
    </div>
  );

  const hero = STATE_HERO[hunt.jurisdiction] || STATE_HERO_DEFAULT;
  const days = hunt.duration_days ? `${hunt.duration_days} days` : "—";

  return (
    <div className="bg-paper min-h-screen">
      <SiteHeader />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="shared-hunt-page">
        <div className="bg-[#12190f] rounded-lg overflow-hidden grain text-bone border border-bone/10">
          <div className="relative h-56 sm:h-72">
            <img src={hero} alt={`${hunt.jurisdiction} landscape`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#12190f] via-[#12190f]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 sm:p-8">
              <span className="hr-overline text-blaze text-[10px]">A HomeRange hunt · shared by {hunt.hunter}</span>
              <h1 className="font-serif text-3xl sm:text-5xl leading-[0.95] mt-1" data-testid="shared-hunt-title">{hunt.title}</h1>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="flex items-center gap-3 border border-bone/15 rounded-md px-4 py-3 bg-bone/[0.03] col-span-2 sm:col-span-1">
                {SPECIES_ICON[hunt.species] ? <img src={SPECIES_ICON[hunt.species]} alt="" className="w-8 h-8 object-contain opacity-90" /> : <Crosshair size={20} className="text-blaze" />}
                <div><span className="text-[11px] text-bone/50 block">Species</span><p className="text-sm font-semibold">{hunt.species || "—"}</p></div>
              </div>
              <Chip icon={MapPin} label="State" value={hunt.jurisdiction} testid="shared-chip-state" />
              <Chip icon={Crosshair} label="Method" value={hunt.method} testid="shared-chip-method" />
              <Chip icon={Backpack} label="Hunt style" value={hunt.hunt_style} testid="shared-chip-style" />
              <Chip icon={Mountain} label="Terrain" value={hunt.terrain} testid="shared-chip-terrain" />
              <Chip icon={CalendarDays} label="Days in field" value={days} testid="shared-chip-days" />
            </div>

            <div className="border-t border-bone/10 mt-7 pt-6 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-serif text-xl">Plan your own hunt like this.</p>
                <p className="text-sm text-bone/55 mt-1">Build a hunt, track readiness, and get gear + draw-odds guidance — free on HomeRange.</p>
              </div>
              <button onClick={() => navigate("/")} className="bg-blaze text-white px-5 py-3 rounded-md text-sm font-semibold inline-flex items-center gap-2 hover:bg-blaze-light transition-colors shrink-0" data-testid="shared-hunt-cta">
                <Compass size={16} /> Build my hunt
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
